import React, { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, UploadCloud, Sparkles, Stamp, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VOUCHER_TYPE_OPTIONS = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'flight', label: 'Flight' },
  { value: 'activity', label: 'Activity' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'itinerary', label: 'Itinerary' },
];

const ACCEPT_EXTRACT = '.pdf,.png,.jpg,.jpeg,.webp';
const ACCEPT_STAMP = '.pdf';

export default function UploadRebrandDialog({ open, onOpenChange, tripId, onCreated }) {
  const [mode, setMode] = useState('extract'); // 'extract' | 'stamp'
  const [voucherType, setVoucherType] = useState('hotel');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [trips, setTrips] = useState([]);
  const [tripsLoaded, setTripsLoaded] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState(tripId || '');
  const [multipage, setMultipage] = useState(false);
  const fileRef = useRef(null);
  const effectiveTripId = useMemo(() => tripId || selectedTripId, [tripId, selectedTripId]);

  // When opened without a tripId, fetch trips so user can pick one for Extract mode
  useEffect(() => {
    if (open && !tripId) {
      setTripsLoaded(false);
      axios.get(`${BACKEND_URL}/api/trips`, { withCredentials: true })
        .then((r) => setTrips(Array.isArray(r.data) ? r.data : (r.data?.trips || [])))
        .catch(() => setTrips([]))
        .finally(() => setTripsLoaded(true));
    }
  }, [open, tripId]);

  const reset = () => {
    setFile(null);
    setBusy(false);
    if (!tripId) setSelectedTripId('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const close = () => {
    if (busy) return;
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Pick a vendor voucher file first');
      return;
    }
    if (mode === 'extract' && !effectiveTripId) {
      toast.error('Pick a trip to attach this voucher to');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (mode === 'extract') {
        fd.append('voucherType', voucherType);
        fd.append('tripId', effectiveTripId);
        const isPdf = (file.type || '').toLowerCase() === 'application/pdf';
        const useMulti = multipage && isPdf;
        const url = useMulti
          ? `${BACKEND_URL}/api/vouchers/rebrand/extract-multipage`
          : `${BACKEND_URL}/api/vouchers/rebrand/extract`;
        if (useMulti) fd.append('splitPerPage', 'true');
        const res = await axios.post(url, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        if (useMulti) {
          const c = res?.data?.createdCount ?? 0;
          const f = (res?.data?.failed || []).length;
          toast.success(`Multi-page: ${c} voucher${c !== 1 ? 's' : ''} created${f ? `, ${f} page${f !== 1 ? 's' : ''} failed` : ''}`);
        } else {
          toast.success('Voucher extracted and re-branded');
        }
        reset();
        onOpenChange(false);
        if (onCreated) onCreated(res.data.voucher);
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/vouchers/rebrand/stamp`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'blob',
          withCredentials: true,
        });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `travcations-rebranded-${(file.name || 'voucher').replace(/\.[^.]+$/, '')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success('Rebranded PDF downloaded');
        reset();
        onOpenChange(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : close())}>
      <DialogContent className="max-w-lg" data-testid="upload-rebrand-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadCloud className="w-5 h-5 text-teal-600" />
            Upload &amp; Auto-Rebrand
          </DialogTitle>
          <DialogDescription>
            Upload any vendor voucher (TBO, Klook, MMT, etc.) and we will re-brand it as Travcations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Mode picker */}
          <div className="grid grid-cols-2 gap-3" data-testid="rebrand-mode-picker">
            <ModeCard
              active={mode === 'extract'}
              onClick={() => setMode('extract')}
              icon={Sparkles}
              title="Extract & Redesign"
              subtitle="OCR vendor voucher, plot data on Travcations layout"
              testId="rebrand-mode-extract"
            />
            <ModeCard
              active={mode === 'stamp'}
              onClick={() => setMode('stamp')}
              icon={Stamp}
              title="Stamp Logo on PDF"
              subtitle="Keep vendor layout, overlay our brand frame"
              testId="rebrand-mode-stamp"
            />
          </div>

          {/* Mode-specific options */}
          {mode === 'extract' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Voucher Type</Label>
                <Select value={voucherType} onValueChange={setVoucherType}>
                  <SelectTrigger data-testid="rebrand-voucher-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VOUCHER_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!tripId && (
                <div className="space-y-2">
                  <Label>Attach to Trip</Label>
                  <Select value={selectedTripId} onValueChange={setSelectedTripId}>
                    <SelectTrigger className={trips.length === 0 ? 'opacity-60' : ''} data-testid="rebrand-trip-select">
                      <SelectValue placeholder={!tripsLoaded ? 'Loading trips…' : (trips.length ? 'Pick a trip…' : 'No trips available')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {trips.map((t) => (
                        <SelectItem key={t.tripId || t._id} value={t.tripId || t._id}>
                          {(t.tripName || t.destination || 'Trip')}
                          {t.tripId ? ` · ${t.displayTripId || t.tripId}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {tripsLoaded && trips.length === 0 && (
                    <p className="text-xs text-slate-500">
                      No trips yet — create a trip in CRM first, or use <strong>Stamp Logo</strong> mode.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* File picker */}
          <div className="space-y-2">
            <Label>Vendor Voucher File</Label>
            <label
              htmlFor="rebrand-file-input"
              className="flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/40 transition"
              data-testid="rebrand-file-dropzone"
            >
              <FileText className="w-8 h-8 text-slate-400" />
              {file ? (
                <div className="text-sm font-medium text-slate-700 text-center break-all">{file.name}</div>
              ) : (
                <>
                  <div className="text-sm font-medium text-slate-700">Click to choose a file</div>
                  <div className="text-xs text-slate-500">
                    {mode === 'stamp' ? 'PDF only · max 15 MB' : 'PDF, PNG, JPG, WebP · max 15 MB'}
                  </div>
                </>
              )}
              <input
                ref={fileRef}
                id="rebrand-file-input"
                type="file"
                accept={mode === 'stamp' ? ACCEPT_STAMP : ACCEPT_EXTRACT}
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="rebrand-file-input"
              />
            </label>
            {mode === 'extract' && file && (file.type || '').toLowerCase() === 'application/pdf' && (
              <label className="flex items-center gap-2 mt-1 text-xs text-slate-600 cursor-pointer" data-testid="multipage-toggle-label">
                <input
                  type="checkbox"
                  checked={multipage}
                  onChange={(e) => setMultipage(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300"
                  data-testid="multipage-toggle"
                />
                <span>
                  <strong>Multi-page mode</strong> — split this PDF into one voucher per page (useful for batch hotel / flight-set bookings).
                </span>
              </label>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy} data-testid="rebrand-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={busy || !file || (mode === 'extract' && !effectiveTripId)}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="rebrand-submit-btn"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</>
            ) : (
              <>{mode === 'extract' ? 'Extract & Save' : 'Generate Rebranded PDF'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModeCard({ active, onClick, icon: Icon, title, subtitle, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-lg border-2 p-3 transition ${
        active
          ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200'
          : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <Icon className={`w-5 h-5 mb-1.5 ${active ? 'text-teal-600' : 'text-slate-500'}`} />
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
    </button>
  );
}
